import React, { useMemo } from 'react';
import { useIntl } from "react-intl";
import { BulkTest, BulkTestStatusType, emptyPage, getPageFromList, Page, BulkTestPageParam } from "@/model";
import { compareDate } from '@/util/dateUtil';
import { useBulkTests } from "@/query/bulkTest";
import { useModal, usePageParam, useScenarioId, useTestProgressHandler } from "@/hook";
import Button from "@/component/Button";
import PaginationBar from "@/component/PaginationBar";
import { SearchBar } from "@/component/input";
import BulkTestTable from './component/BulkTestTable';
import TestUploadModal, { TEST_UPLOAD_MODAL_ID } from "./component/TestUploadModal";
import { AddIcon } from "@/assets/svgs/icons";


const STATUS_ORDER: Record<BulkTestStatusType, number> = {
    done: 0,
    cancel: 1,
    failed: 2,
    progress: 9
};

const BULK_TEST_PAGE_SIZE = 15;

const BulkTestView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    const { param, setParam } = usePageParam<BulkTestPageParam>({
        page: 0,
        size: BULK_TEST_PAGE_SIZE,
        sortKey: "created_at",
        sort: "desc",
    });

    const { data: bulkTests, refetch } = useBulkTests(scenarioId);

    const { testingId, testingProgress } = useTestProgressHandler(refetch);

    const onClickSortHead = (key: BulkTestPageParam["sortKey"]) => {
        if (param.sortKey === key) {
            setParam({
                sort: param.sort === "asc" ? "desc" : "asc"
            });
        } else {
            setParam({
                sortKey: key
            });
        }
    };

    const bulkTestPage = useMemo<Page<BulkTest>>(() => {
        if (!bulkTests?.length) {
            return emptyPage<BulkTest>();
        }
        let sorted = 
            bulkTests
                .filter(bulkTest => (
                    bulkTest.title?.toLowerCase().includes((param.keyword ?? "").toLowerCase())
                ))
                .sort((a, b) => {
                    if (param.sortKey === "created_at") {
                        return compareDate(a.created_at, b.created_at);
                    } else if (param.sortKey === "status") {
                        return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
                    } else {
                        return 0;
                    }
                });
        if (param.sort === "desc") {
            sorted = sorted.reverse();
        }

        return getPageFromList(sorted, param.page, param.size);
    }, [bulkTests, param]);

    return (<>
        <TestUploadModal />

        <main className="scenario-content-layout"
        >
            <div className="w-md-page max-h-full flex flex-col gap-3">
                <div className="search-bar-container">
                    <SearchBar width={680}
                               formAriaLabel={intl.formatMessage({ id: "m04" })}
                               placeholder={intl.formatMessage({ id: "i05007" })}
                               onSearch={keyword => setParam({ keyword })}
                    />

                    <div className="search-bar-container-btn-group">
                        <Button variant="text"
                                onClick={() => modal.open(TEST_UPLOAD_MODAL_ID)}
                        >
                            <AddIcon aria-hidden="true"
                                     className="size-3.5"
                            />
                            {intl.formatMessage({ id: "i05001" })}
                        </Button>
                    </div>
                </div>

                <div className="table-pagination-container">
                    <BulkTestTable pageData={bulkTestPage}
                                   pageParam={param}
                                   testingId={testingId}
                                   testingProgress={testingProgress}
                                   onClickSortHead={onClickSortHead}
                    />

                    <PaginationBar pageData={bulkTestPage}
                                   onClickPage={page => setParam({ page })}
                    />
                </div>
            </div>
        </main>
    </>);
};

export default BulkTestView;
